import { z } from 'zod';
import { stringifyLosslessJson } from '@pillar-portal/api-types/lossless-json-codec';
import { createPillarApiWithOptions, LogIngestRequestSchema, RequestLogEntrySchema, } from '@pillar-portal/api-types/samba';
const TraceHeaderSchema = z.string().min(1);
const MaxBatchSize = 10;
const MaxBatchBytes = 64 * 1024;
const textEncoder = new TextEncoder();
const BatchEnvelopeBytes = textEncoder.encode('{"entries":[]}').byteLength;
export class PillarLogTransportCore {
    getPortalUrl;
    getRequestHeaders;
    getTraceContextHeader;
    customFetch;
    getRequestAbortSignal;
    pendingLogBatch = null;
    constructor(config) {
        this.getPortalUrl = config.getPortalUrl;
        this.getRequestHeaders = config.getRequestHeaders ?? (async () => ({}));
        this.getTraceContextHeader = config.getTraceContextHeader ?? (() => null);
        this.customFetch = config.customFetch;
        this.getRequestAbortSignal = config.getRequestAbortSignal;
    }
    async send(entry, options) {
        const requestLogEntry = RequestLogEntrySchema.parse(entry);
        const portalUrl = this.getPortalUrl();
        const traceHeaderValue = this.getTraceContextHeader();
        const requestAbortSignal = this.getRequestAbortSignal?.();
        const httpRequestHeaders = { ...(await this.getRequestHeaders()) };
        if (options.awaitWrite) {
            await this.writeBatch([requestLogEntry], portalUrl, httpRequestHeaders, traceHeaderValue, requestAbortSignal);
            return;
        }
        const requestKey = stringifyLosslessJson([
            portalUrl,
            traceHeaderValue,
            Object.keys(httpRequestHeaders).map((headerName) => [
                headerName,
                httpRequestHeaders[headerName],
            ]),
        ]);
        const entryBytes = textEncoder.encode(stringifyLosslessJson(requestLogEntry)).byteLength + 1;
        let pendingLogBatch = this.pendingLogBatch;
        if (pendingLogBatch === null ||
            pendingLogBatch.requestKey !== requestKey ||
            pendingLogBatch.requestAbortSignal !== requestAbortSignal ||
            pendingLogBatch.byteLength + entryBytes > MaxBatchBytes) {
            const entries = [];
            const completion = Promise.resolve().then(async () => {
                if (this.pendingLogBatch?.entries === entries)
                    this.pendingLogBatch = null;
                await this.writeBatch(entries, portalUrl, httpRequestHeaders, traceHeaderValue, requestAbortSignal);
            });
            pendingLogBatch = {
                requestKey,
                entries,
                completion,
                requestAbortSignal,
                byteLength: BatchEnvelopeBytes,
            };
            this.pendingLogBatch = pendingLogBatch;
        }
        pendingLogBatch.entries.push(requestLogEntry);
        pendingLogBatch.byteLength += entryBytes;
        if (pendingLogBatch.entries.length === MaxBatchSize ||
            pendingLogBatch.byteLength >= MaxBatchBytes)
            this.pendingLogBatch = null;
        await pendingLogBatch.completion;
    }
    async sendBatch(entries, _options) {
        const logIngestRequest = LogIngestRequestSchema.parse({ entries });
        const portalUrl = this.getPortalUrl();
        const traceHeaderValue = this.getTraceContextHeader();
        const requestAbortSignal = this.getRequestAbortSignal?.();
        const httpRequestHeaders = { ...(await this.getRequestHeaders()) };
        await this.writeBatch(logIngestRequest.entries, portalUrl, httpRequestHeaders, traceHeaderValue, requestAbortSignal);
    }
    async writeBatch(entries, portalUrl, httpRequestHeaders, traceHeaderValue, requestAbortSignal) {
        const payload = { entries };
        const traceHeader = traceHeaderValue
            ? TraceHeaderSchema.parse(traceHeaderValue)
            : undefined;
        const fetchImplementation = this.customFetch ?? globalThis.fetch;
        const pillarApi = createPillarApiWithOptions({
            baseUrl: buildApiBaseUrl(portalUrl),
            fetchImplementation: async (input, init) => {
                requestAbortSignal?.throwIfAborted();
                return await fetchImplementation(input, {
                    ...init,
                    signal: requestAbortSignal === undefined
                        ? init?.signal
                        : init?.signal == null
                            ? requestAbortSignal
                            : AbortSignal.any([requestAbortSignal, init.signal]),
                });
            },
            getRequestHeaders: () => {
                return {
                    ...httpRequestHeaders,
                    ...(traceHeader ? { 'X-Cloud-Trace-Context': traceHeader } : {}),
                };
            },
        });
        await pillarApi.loggingApi.ingestLogs(payload);
    }
}
function buildApiBaseUrl(portalUrl) {
    const baseUrl = portalUrl.endsWith('/') ? portalUrl : `${portalUrl}/`;
    const url = new URL('api', baseUrl);
    return url.toString();
}
//# sourceMappingURL=index.js.map